import React from 'react';
import { Link } from 'react-router-dom';
import LocalHelmet from '../common/LocalHelmet';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Flywheel from '../components/Flywheel';
import DynamicHeroImage from '../components/DynamicHeroImage';
import * as FiIcons from 'react-icons/fi';
import siteData from '../content/siteData.json';

const { FiArrowRight, FiTarget, FiBarChart, FiCpu, FiTrendingUp, FiUsers, FiShield } = FiIcons;

const Home = () => {
  const { hero, services, results, values, cta } = siteData;

  const servicesList = [
    {
      icon: FiTarget,
      title: 'Growth Strategy',
      description: 'Direct support for CMOs, CEOs, and boards on profitable growth decisions.'
    },
    {
      icon: FiBarChart,
      title: 'Strategic Measurement',
      description: 'Build and scale decision systems that tie investment to outcomes through attribution, incrementality, marketing mix modeling, forecasting, and more.'
    },
    {
      icon: FiCpu,
      title: 'AI in Practice',
      description: 'Scalable programs with Claude, Gemini, Perplexity, ChatGPT and more to accelerate insights.'
    },
    {
      icon: FiTrendingUp,
      title: 'Data in Action',
      description: 'Turn complex data into clear decisions with frameworks designed for speed, clarity, and real-world execution.'
    },
    {
      icon: FiUsers,
      title: 'Fractional Leadership',
      description: 'Executive strategy coverage and stability when you need it most.'
    }
  ];

  const iconMap = {
    FiTarget,
    FiShield,
    FiUsers
  };

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
      <title>Home | Growth Strategy & Analytics | Richardson Strategy Group</title>
      <meta
        name="description"
        content="You make growth decisions every day. Richardson Strategy Group helps executives do more of what matters—through strategy, analytics, and real-world clarity."
      />
      <link rel="canonical" href="https://richardsonstrategy.group/" />
      {/* Open Graph */}
      <meta property="og:site_name" content="Richardson Strategy Group" />
      <meta property="og:type" content="website" />
      <meta
        property="og:title"
        content="Growth Strategy & Analytics | Richardson Strategy Group"
      />
      <meta
        property="og:description"
        content="Unlock clarity for fast-moving teams. Strategy and analytics for executives ready to do more."
      />
      <meta property="og:url" content="https://richardsonstrategy.group/" />
      <meta
        property="og:image"
        content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-default.jpg"
      />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Growth Strategy & Analytics | Richardson Strategy Group"
      />
      <meta
        name="twitter:description"
        content="Clarity for every decision. Growth and analytics consulting built for leaders."
      />
      <meta
        name="twitter:image"
        content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/twitter-card.jpg"
      />
      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Richardson Strategy Group',
          url: 'https://richardsonstrategy.group',
          logo: 'https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/brand/rsg-logo.svg',
          description:
            "Growth strategy and analytics consulting for executives and boards. Real clarity, faster progress.",
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'business',
            url: 'https://richardsonstrategy.group/contact',
          },
          sameAs: [
            'https://www.linkedin.com/company/richardson-strategy-group',
          ],
        })}
      </script>
    </LocalHelmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-secondary-900 mb-4 md:mb-6 leading-tight"
                >
                  {hero.title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-lg sm:text-xl md:text-2xl text-secondary-600 mb-6 md:mb-8 leading-relaxed"
                >
                  {hero.subtitle}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link
                    to="/contact"
                    className="bg-primary-600 text-white px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 text-center"
                  >
                    <span>{hero.primaryCTA}</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/about"
                    className="border border-secondary-300 text-secondary-700 px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-secondary-50 transition-colors text-center"
                  >
                    {hero.secondaryCTA}
                  </Link>
                </motion.div>
              </div>
              
              {/* Dynamic Hero Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative"
              >
                <DynamicHeroImage 
                  className="w-full"
                  fallbackImage={hero.heroImage}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-4 md:mb-6">
                {services.title}
              </h2>
              <p className="text-base md:text-lg text-secondary-600 max-w-3xl mx-auto leading-relaxed">
                {services.subtitle}
              </p>
            </div>

            {/* Flywheel Visual */}
            <div className="mb-12 md:mb-16">
              <Flywheel />
              <div className="text-center mt-6 md:mt-8">
                <p className="text-base md:text-lg text-secondary-600 italic">
                  {services.tagline}
                </p>
              </div>
            </div>

            {/* Services Grid - Centered Layout */}
            <div className="max-w-5xl mx-auto">
              {/* First row - 3 items */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
                {servicesList.slice(0, 3).map((service, index) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100 hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                      <SafeIcon icon={service.icon} className="w-5 h-5 md:w-6 md:h-6 text-primary-600" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-secondary-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-secondary-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>
              
              {/* Second row - 2 items centered */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-2xl mx-auto">
                {servicesList.slice(3, 5).map((service, index) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: (index + 3) * 0.1 }}
                    className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100 hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                      <SafeIcon icon={service.icon} className="w-5 h-5 md:w-6 md:h-6 text-primary-600" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-secondary-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-secondary-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="text-center mt-8 md:mt-12">
              <Link
                to="/services"
                className="text-primary-600 font-medium hover:text-primary-700 transition-colors inline-flex items-center space-x-2"
              >
                <span>Explore all our services</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="bg-secondary-50 py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-4 md:mb-6">
                Why Work With Us
              </h2>
              <p className="text-base md:text-lg text-secondary-600 max-w-3xl mx-auto leading-relaxed">
                Two decades in analytics and strategy. Built and scaled teams from 5 to 125+. Results that matter: stronger margins, faster planning, higher board confidence.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {results.map((result, index) => (
                <motion.div
                  key={result.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                    {result.metric}
                  </div>
                  <div className="text-secondary-600 font-medium text-sm md:text-base">
                    {result.label}
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Disclaimer */}
            <div className="text-center mt-6 md:mt-8">
              <p className="text-xs md:text-sm text-secondary-500">
                * Results not guaranteed but representative of typical client outcomes
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-4 md:mb-6">
                  {values.title}
                </h2>
                <div className="space-y-4 md:space-y-6">
                  {values.items.map((item, index) => {
                    const IconComponent = iconMap[item.icon];
                    return (
                      <div key={item.title} className="flex items-start space-x-3">
                        <SafeIcon 
                          icon={IconComponent} 
                          className="w-5 h-5 md:w-6 md:h-6 text-primary-600 mt-1" 
                        />
                        <div>
                          <h3 className="font-semibold text-secondary-900 mb-2">
                            {item.title}
                          </h3>
                          <p className="text-secondary-600 leading-relaxed text-sm md:text-base">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-primary-50 p-6 md:p-8 rounded-2xl">
                <blockquote className="text-base md:text-lg text-secondary-700 italic mb-4">
                  "{values.quote.text}"
                </blockquote>
                <cite className="text-secondary-600 font-medium">
                  — {values.quote.author}
                </cite>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary-600 py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
              {cta.title}
            </h2>
            <p className="text-lg md:text-xl text-primary-100 mb-6 md:mb-8 leading-relaxed">
              {cta.subtitle}
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-secondary-50 transition-colors inline-flex items-center space-x-2"
            >
              <span>{cta.buttonText}</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;